import { useState, useMemo } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Button,
  TextField,
  MenuItem,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridPaginationModel,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import {
  Add,
  Search,
  Edit,
  Visibility,
  Business,
} from '@mui/icons-material';
import { useEmpresas } from '../../hooks/useEmpresas';
import { useAuth } from '../../context/AuthContext';

// `profile` ya no se usa directamente; effectiveRole cubre todos los casos.
import {
  ESTADO_EMPRESA_LABELS,
  ESTADO_EMPRESA_COLORS,
  ESTADO_EMPRESA_OPTIONS,
  type Empresa,
  type EstadoEmpresa,
} from '../../types/empresa';
import { formatCuit, formatDate } from '../../utils/format';

export default function EmpresasList() {
  const navigate = useNavigate();
  const { effectiveRole, startImpersonation } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [estado, setEstado] = useState<EstadoEmpresa | 'todas'>('todas');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  // Para admin_empresa (real o impersonando) no tiene sentido el listado.
  // El query se evita poniendo isAdminEmpresa abajo, y retornamos <Navigate>
  // despues de declarar todos los hooks (Rules of Hooks).
  const isAdminEmpresa = effectiveRole === 'admin_empresa';

  const { data, isLoading, isError, error } = useEmpresas({
    search,
    estado,
    page: paginationModel.page,
    pageSize: paginationModel.pageSize,
  });

  // Usamos effectiveRole para que un admin impersonando no vea acciones de admin
  const canCreate = effectiveRole === 'admin_general' || effectiveRole === 'operador';
  const canImpersonate = effectiveRole === 'admin_general';

  const handleSearch = () => setSearch(searchInput);
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleImpersonate = (empresa: Empresa) => {
    startImpersonation(empresa.id);
    navigate('/');
  };

  const columns = useMemo<GridColDef<Empresa>[]>(
    () => [
      {
        field: 'cuit',
        headerName: 'CUIT',
        width: 150,
        renderCell: (params) => (
          <Box sx={{ fontFamily: 'monospace' }}>{formatCuit(params.value)}</Box>
        ),
      },
      {
        field: 'razon_social',
        headerName: 'Razon Social',
        flex: 1,
        minWidth: 220,
        renderCell: (params) => (
          <Box sx={{ fontWeight: 500 }}>{params.value}</Box>
        ),
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1,
        minWidth: 180,
        renderCell: (params) => params.value || '—',
      },
      {
        field: 'fecha_alta',
        headerName: 'Alta',
        width: 110,
        renderCell: (params) => formatDate(params.value as string | null),
      },
      {
        field: 'convenio_2_porc',
        headerName: 'Convenio',
        width: 100,
        renderCell: (params: GridRenderCellParams<Empresa>) =>
          params.value ? (
            <Chip label="2%" size="small" color="primary" variant="outlined" />
          ) : (
            <Chip label="—" size="small" variant="outlined" />
          ),
      },
      {
        field: 'estado',
        headerName: 'Estado',
        width: 120,
        renderCell: (params: GridRenderCellParams<Empresa>) => (
          <Chip
            label={ESTADO_EMPRESA_LABELS[params.value as EstadoEmpresa]}
            size="small"
            color={ESTADO_EMPRESA_COLORS[params.value as EstadoEmpresa]}
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'Acciones',
        width: canImpersonate ? 140 : 100,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<Empresa>) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Ver detalle">
              <IconButton
                size="small"
                onClick={() => navigate(`/empresas/${params.row.id}`)}
              >
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Editar">
              <IconButton
                size="small"
                onClick={() => navigate(`/empresas/${params.row.id}/editar`)}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            {canImpersonate && (
              <Tooltip title="Ver como empresa">
                <IconButton
                  size="small"
                  color="warning"
                  onClick={() => handleImpersonate(params.row)}
                >
                  <Business fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    [canImpersonate, navigate]
  );

  // Early return DESPUES de todos los hooks (Rules of Hooks).
  if (isAdminEmpresa) {
    return <Navigate to="/mi-empresa" replace />;
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Empresas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Listado de empresas afiliadas al sindicato
          </Typography>
        </Box>
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/empresas/nuevo')}
          >
            Nueva Empresa
          </Button>
        )}
      </Stack>

      <Card sx={{ mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ p: 2 }}
        >
          <TextField
            size="small"
            placeholder="Buscar por razon social o CUIT..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            size="small"
            select
            label="Estado"
            value={estado}
            onChange={(e) => {
              setEstado(e.target.value as EstadoEmpresa | 'todas');
              setPaginationModel((m) => ({ ...m, page: 0 }));
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="todas">Todas</MenuItem>
            {ESTADO_EMPRESA_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="outlined" onClick={handleSearch} startIcon={<Search />}>
            Buscar
          </Button>
        </Stack>
      </Card>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar empresas: {(error as Error)?.message ?? 'desconocido'}
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={data?.data ?? []}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id}
          paginationMode="server"
          rowCount={data?.count ?? 0}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          autoHeight
          sx={{
            border: 0,
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: 'background.default',
            },
            // En mobile, dejamos scroll horizontal con todas las columnas
            ...(isMobile && { '& .MuiDataGrid-main': { overflowX: 'auto' } }),
          }}
          localeText={{
            noRowsLabel: 'No hay empresas registradas',
            footerRowSelected: (count) => `${count} fila(s) seleccionada(s)`,
            footerTotalRows: 'Total:',
            MuiTablePagination: {
              labelRowsPerPage: 'Por pagina:',
              labelDisplayedRows: ({ from, to, count }) =>
                `${from}-${to} de ${count}`,
            },
          }}
        />
      </Card>
    </Box>
  );
}
